/**
 * Certificates for completed courses (Aula).
 *
 * ISSUANCE is server-authoritative: when the last passing lesson is completed,
 * the write-path checks the completion criterion and seals a certificate
 * (snapshot of learner/course/version/academy) with a verifiable UUID id. The
 * client receives it via the private `my_certificates` stream and READS it
 * offline (never writes it). This module provides:
 *   - the pure MIRROR of the completion criterion (for the local-first UI);
 *   - the certificate CONTENT template (printable HTML → OS PDF);
 *   - the REST client for public verification (guest-first, no account).
 */

import { and, certificate, eq, isNull, type PowerSyncDrizzleDB } from "@aula/powersync";
import { LESSON_PASS_THRESHOLD } from "./progress";

/** Minimal lesson row for the criterion (subset of `lesson`). */
export interface CertificateLessonRow {
  id: string;
  deleted_at?: string | null;
}

/** Minimal progress row for the criterion (subset of `lesson_progress`). */
export interface CertificateProgressRow {
  lesson_id: string | null;
  status: string | null;
  score: number | null;
}

/**
 * Course COMPLETION criterion (pure mirror of the server):
 * the course has ≥ 1 live lesson and ALL of them are `completed` with a passing
 * score (score >= LESSON_PASS_THRESHOLD — the authoritative one the server
 * re-grades). A course with no lessons is NEVER complete (the void is not
 * certified).
 */
export function isCourseCompleted(
  lessons: readonly CertificateLessonRow[],
  progress: readonly CertificateProgressRow[],
): boolean {
  const live = lessons.filter((l) => l.deleted_at == null);
  if (live.length === 0) {
    return false;
  }
  const passed = new Set(
    progress
      .filter((p) => p.status === "completed" && (p.score ?? 0) >= LESSON_PASS_THRESHOLD)
      .map((p) => p.lesson_id),
  );
  return live.every((l) => passed.has(l.id));
}

/** Minimal certificate data consumed by the template. */
export interface CertificateData {
  id: string;
  learner_name?: string | null;
  course_title?: string | null;
  course_version?: number | null;
  org_name?: string | null;
  issued_at?: string | null;
}

/** Renderable content of the certificate template. */
export interface CertificateContent {
  certId: string;
  learnerName: string;
  courseTitle: string;
  /** Course version seal at issuance (authorship audit). */
  versionLabel: string;
  /** Issuing academy, or `null` if the course was not published via organization. */
  orgName: string | null;
  /** Issue date formatted for the given locale. */
  issuedAtLabel: string;
  /** Public verification URL (guest-first `/verify/:id` page). */
  verifyUrl: string;
}

/**
 * Certificate CONTENT template (pure and testable): normalizes the synced
 * snapshot to the fields the printable view and the verification page paint.
 * `verifyBaseUrl` = front origin (e.g. window.origin).
 */
export function buildCertificateContent(
  cert: CertificateData,
  opts: { verifyBaseUrl: string; locale?: string },
): CertificateContent {
  const issuedAt = cert.issued_at ? new Date(cert.issued_at) : null;
  const issuedAtLabel =
    issuedAt && !Number.isNaN(issuedAt.getTime())
      ? new Intl.DateTimeFormat(opts.locale ?? "en", { dateStyle: "long" }).format(issuedAt)
      : "";
  return {
    certId: cert.id,
    learnerName: cert.learner_name ?? "",
    courseTitle: cert.course_title ?? "",
    versionLabel: `v${cert.course_version ?? 1}`,
    orgName: cert.org_name ?? null,
    issuedAtLabel,
    verifyUrl: `${opts.verifyBaseUrl.replace(/\/$/, "")}/verify/${encodeURIComponent(cert.id)}`,
  };
}

// ─── Local-first reads (PowerSync) ────────────────────────────────────────

/** My certificates (private, synced), most recent first. */
export function getMyCertificatesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(certificate)
    .where(and(eq(certificate.user_id, userId), isNull(certificate.revoked_at)));
}

/** The user's certificate for ONE course (single row, if already issued). */
export function getCourseCertificateQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  courseId: string,
) {
  return db
    .select()
    .from(certificate)
    .where(
      and(
        eq(certificate.user_id, userId),
        eq(certificate.course_id, courseId),
        isNull(certificate.revoked_at),
      ),
    )
    .limit(1);
}

/** One own certificate by id (for the printable view). */
export function getCertificateByIdQuery(db: PowerSyncDrizzleDB, certId: string) {
  return db.select().from(certificate).where(eq(certificate.id, certId)).limit(1);
}

// ─── Public verification (REST, guest-first) ────────────────────────────────

/** camelCase DTO of `models/course.rs::Certificate` (kept by hand). */
export interface CertificateDTO {
  id: string;
  learnerName: string;
  courseTitle: string;
  courseVersion: number;
  orgName: string | null;
  issuedAt: string;
}

/** DTO of `models/course.rs::CertificateVerification`. */
export interface CertificateVerificationDTO {
  valid: boolean;
  certificate: CertificateDTO | null;
}

/**
 * Verifies a certificate by id against the server (public, no account).
 * `valid=false` covers both "does not exist" and "revoked" — without
 * filtering anything else.
 */
export async function fetchCertificateVerification(
  baseUrl: string,
  certId: string,
  opts: { signal?: AbortSignal } = {},
): Promise<CertificateVerificationDTO> {
  const res = await fetch(
    `${baseUrl.replace(/\/$/, "")}/api/certificates/${encodeURIComponent(certId)}/verify`,
    { signal: opts.signal },
  );
  if (!res.ok) {
    throw new Error(`certificate verification failed: ${res.status}`);
  }
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- trust-boundary parse of our server's typed DTO
  return (await res.json()) as CertificateVerificationDTO;
}
